import {
  Badge,
  Button,
  createListCollection,
  Field,
  HStack,
  IconButton,
  Input,
  Portal,
  Select,
  Table,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaRegCopy } from "react-icons/fa6";
import { IoCheckmarkDoneOutline } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { depositList, refund } from "../Redux/Action";
import { WithdrawalSkeleton } from "../Components/custom/Skeletons/WithdrawalSkeleton";
import { formatDateTime } from "../Utils/newHelper";
import { AddressCardDialog } from "../Components/custom/AddressCardDialog";
import { CustomPagination } from "../Components/custom/CustomPagination";
import DateRangeFilter from "../Components/custom/DateRangeFilter";
import { getLocalStorageItem } from "../Utils/Helper";
import { setDynamicTitle } from "../Utils/dynamicTitleManager";

export const DepositRequests = () => {
  // Use dynamic title management
  useEffect(() => {
    setDynamicTitle("Deposit Requests");
  }, []);

  const statuses = createListCollection({
    items: [
      { label: "All", value: "all" },
      { label: "Pending", value: "pending" },
      { label: "Rejected", value: "rejected" },
      { label: "Declined", value: "declined" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Success", value: "success" },
    ],
  });

  const dispatch = useDispatch();
  const [allDeposits, setAllDeposits] = useState([]);
  const [isDataLoading, setisDataLoading] = useState(true);
  const [isRefundID, setisRefundID] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [addressCardDialogOpen, setAddressCardDialogOpen] = useState(false);
  const [addressCardData, setAddressCardData] = useState({});
  const [addressCardType, setAddressCardType] = useState("");
  const [IP, setIP] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(formatDate(oneDayAgo));
  const [to, setTo] = useState(formatDate(now));
  const [status, setStatus] = useState("all");
  const today = now.toISOString().split("T")[0];
  const thirtyOneDaysAgo = new Date(now.getTime() - 31 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  const whitelabelData = getLocalStorageItem("whitelabelData");
  function formatDate(date) {
    return date.toISOString().split("T")[0];
  }

  useEffect(() => {
    const payload = {
      ...(whitelabelData?._id != null && { whitelabelId: whitelabelData._id }),
      date_from: from,
      date_to: to,
      per_page: 10,
      page: currentPage,
      currency: "USD",
      ...(status && { status }),
    };
    dispatch(
      depositList({
        payload,
        callback: (data) => {
          if (data?.meta?.code === 200) {
            setAllDeposits(data?.data?.data);
            setTotalPages(data?.data?.pagination?.totalPages);
            setisDataLoading(false);
          }
        },
      })
    );
  }, [currentPage, from, to, status]);

  // FIXED: use the correct transaction id (the local variable transactionID)
  const handleCopy = async (id) => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      // only clear if the id is still the same (prevents races)
      const captured = id;
      setTimeout(() => {
        setCopiedId((prev) => (prev === captured ? null : prev));
      }, 1000);
    } catch (err) {
      // optional: keep behavior unchanged except logging
      console.error("Failed to copy to clipboard:", err);
    }
  };

  return (
    <VStack>
      <HStack width="100%" justifyContent="space-between">
        <DateRangeFilter
          initialFrom={from}
          initialTo={to}
          onApply={({ from, to }) => {
            setFrom(from);
            setTo(to);
          }}
          isLoading={isDataLoading}
        />
        <HStack width="30%" justifyContent="flex-end" spacing={4}>
          <Select.Root
            collection={statuses}
            width="100%"
            disabled={isDataLoading}
            onValueChange={(details) => {
              setStatus(details.value[0]);
              setCurrentPage(1);
            }}
            value={[status]} // Value should be an array
            defaultValue={["all"]}
          >
            <Select.HiddenSelect />
            <Select.Label>Select Status</Select.Label>
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder="Status" />
              </Select.Trigger>
              <Select.IndicatorGroup>
                <Select.ClearTrigger />
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
              <Select.Positioner>
                <Select.Content>
                  {statuses.items.map((status) => (
                    <Select.Item item={status} key={status.value}>
                      {status.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>
        </HStack>
      </HStack>
      {isDataLoading ? (
        <WithdrawalSkeleton />
      ) : (
        <>
          <Table.ScrollArea borderWidth="1px" rounded="md" maxHeight="70vh" width="100%">
            <Table.Root interactive variant="outline" stickyHeader>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader width="10%">Name</Table.ColumnHeader>
                  <Table.ColumnHeader width="10%" textAlign="center">
                    Amount
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="10%" textAlign="center">
                    Status
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="15%" textAlign="center">
                    Transaction ID
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="10%" textAlign="center">
                    Deposit Method
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="10%" textAlign="center">
                    Deposit Date
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="5%" textAlign="center">
                    Action
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {allDeposits?.map((item) => {
                  const transactionID = item?.reference ?? item?.sessionId;
                  return (
                    <Table.Row key={item._id}>
                      <Table.Cell>
                        {item?.user?.fname} {item?.user?.lname}
                      </Table.Cell>
                      <Table.Cell textAlign="center">{item.amount}</Table.Cell>
                      <Table.Cell textAlign="center">
                        <Badge
                          variant="surface"
                          colorPalette={
                            item.status === "success"
                              ? "green"
                              : item.status === "pending"
                              ? "yellow"
                              : item.status === "failed" || item.status === "cancelled"
                              ? "red"
                              : "gray"
                          }
                        >
                          {item.status}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <HStack justifyContent="center">
                          {transactionID?.length > 8
                            ? `${transactionID?.slice(0, 4)}...${transactionID?.slice(-4)}`
                            : transactionID}
                          <IconButton
                            variant="surface"
                            size="xs"
                            colorPalette="teal"
                            onClick={() => handleCopy(transactionID)}
                          >
                            {copiedId === transactionID ? <IoCheckmarkDoneOutline /> : <FaRegCopy />}
                          </IconButton>
                        </HStack>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Badge variant="surface" colorPalette="teal" size="sm">
                          {item.method}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell textAlign="center" fontSize="xs">
                        {formatDateTime(item.createdAt)}
                      </Table.Cell>
                      <Table.Cell textAlign="center">-</Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
          <CustomPagination totalCount={totalPages} current={currentPage} setCurrent={setCurrentPage} />
        </>
      )}
      {addressCardDialogOpen && (
        <AddressCardDialog
          data={addressCardData}
          ip={IP}
          type={addressCardType}
          addressCardDialogOpen={addressCardDialogOpen}
          setAddressCardDialogOpen={setAddressCardDialogOpen}
        />
      )}
    </VStack>
  );
};